import type { BlueprintVariant } from "@/components/marketing/ServiceBlueprint";

export interface ServiceCopy {
  slug: "google-ads" | "meta-ads" | "local-services-ads" | "youtube-ads" | "seo-ai-search";
  idx: string;
  title: string;
  italicSuffix?: string;
  tag: string;
  short: string;
  to: string;
  description: string;
  /** Overrides the generated <title> / meta description for search intent. */
  seoTitle?: string;
  seoDescription?: string;
  whatItIs: string;
  howWeRunIt: { title: string; body: string }[];
  deliverables: string[];
  industriesFit: string[];
  blueprint: BlueprintVariant;
}

export const SERVICES: ServiceCopy[] = [
  {
    slug: "google-ads",
    idx: "01",
    title: "Google Ads",
    tag: "Certified Google Partner",
    to: "/services/google-ads",
    short:
      "The full Google Ads system (Search, Performance Max, Display, Demand Gen, Video, Remarketing, and Local Campaigns) run as one integrated growth engine. Conversion tracking that actually fires, dynamic call tracking, and bid signals built to feed the algorithm.",
    description:
      "Search, Performance Max, Display, Demand Gen, Video and Local campaigns built for operators who measure outcomes, one integrated Google Ads system.",
    seoDescription:
      "Search, Performance Max and Demand Gen run against one budget, with offline conversion imports and call tracking so bidding learns from booked work.",
    whatItIs:
      "We run the full Google Ads system as one account: Search, Performance Max, Display, Demand Gen, Video, Remarketing and Local campaigns. Every account is built on offline conversion data and dynamic call tracking, so bidding learns from booked jobs, not page views.",
    howWeRunIt: [
      {
        title: "Conversion tracking that fires",
        body: "GA4, dynamic call tracking (CallRail), offline conversion imports from your CRM, and value-based bidding wherever margins justify it.",
      },
      {
        title: "Campaign architecture built to scale",
        body: "Service- and geo-segmented Search, PMax asset groups built around your services and geos, and Local Campaigns for store-visit and call objectives where the buyer journey is local.",
      },
      {
        title: "PMax with guardrails",
        body: "Asset groups built around your services and geos, audience signals seeded from your CRM, and negative keyword scripts so PMax doesn't cannibalize Search.",
      },
      {
        title: "Upper-funnel and re-engagement",
        body: "Display, Demand Gen, and Video build awareness in your real market; Remarketing closes the loop on visitors who didn't convert. Each layer is measured against assisted conversions and branded-search lift, not view counts.",
      },
      {
        title: "Weekly optimization cycle",
        body: "Search term reviews, bid signal tuning, creative refresh, and disapproval triage every week, not quarterly check-ins.",
      },
    ],
    deliverables: [
      "Account audit + tracking diagnostic",
      "Full-system campaign architecture (Search, PMax, Display, Demand Gen, Video, Remarketing, Local Campaigns)",
      "GA4 + dynamic call tracking",
      "Offline conversion pipeline",
      "Weekly optimization & reporting",
      "Direct platform escalation via Partner support",
    ],
    industriesFit: ["Home services", "Roofing", "Pool services", "Landscaping", "Construction", "Consumer goods", "Business services", "Food & beverage", "Sports & fitness"],
    blueprint: "google",
  },
  {
    slug: "meta-ads",
    idx: "02",
    title: "Meta Ads:",
    italicSuffix: "Facebook & Instagram",
    tag: "Meta Business Partner",
    to: "/services/meta-ads",
    short:
      "Creative-led acquisition for service businesses and consumer brands. We test offers, hooks, and formats weekly, not quarterly. Optimized against link clicks and offline events.",
    description:
      "Creative-led paid social for operators. Weekly hook and offer tests, optimized against offline lead events.",
    seoTitle: "Facebook & Instagram Ads Agency in Phoenix and Scottsdale",
    seoDescription:
      "Meta ads for home services and local businesses, run from Scottsdale. Campaigns are judged on booked jobs and qualified calls, not clicks.",
    whatItIs:
      "Facebook and Instagram acquisition for operators who measure on outcomes, run from our Scottsdale base across the Phoenix metro. Creative is the lever, not targeting, and we test against the events that correlate with revenue.",
    howWeRunIt: [
      {
        title: "Creative as the variable",
        body: "Weekly hook, offer, and format tests. Static, UGC-style video, and direct response: produced or sourced for your brand.",
      },
      {
        title: "Optimized against offline events",
        body: "We push booked work and qualified leads back to Meta via CAPI so optimization isn't chasing form-fills that never convert.",
      },
      {
        title: "Audience discipline",
        body: "Service-area exclusions where relevant, broad audiences with creative-led targeting, and lookalikes seeded from your real customer base, not your form-fill list.",
      },
      {
        title: "Sales-cycle aware",
        body: "Retargeting funnels tuned to your category's real consideration windows: weeks for high-ticket services, days for consumer.",
      },
    ],
    deliverables: [
      "Account audit + Pixel/CAPI diagnostic",
      "Creative brief and weekly production cycle",
      "Campaign architecture & build",
      "Offline event integration",
      "Weekly optimization & reporting",
    ],
    industriesFit: ["Home services", "Consumer goods", "Food & beverage", "Sports & fitness", "Cleaning", "Roofing", "Painting", "Landscaping"],
    blueprint: "meta",
  },
  {
    slug: "local-services-ads",
    idx: "03",
    title: "Local Services Ads",
    tag: "Lead-level attribution",
    to: "/services/local-services-ads",
    short:
      "Google Verified placements above the search results. Verification, lead review and credits, and ongoing optimization, not set-and-forget.",
    description:
      "Google Verified placements above paid search. End-to-end verification, lead review and credits, and ongoing optimization.",
    seoTitle: "Local Services Ads Management for Contractors in Arizona | Berk Hospitality Group",
    seoDescription:
      "We run Google Local Services Ads for home-services businesses in Arizona and California, including lead review and credits and Google Verified setup.",
    whatItIs:
      "Local Services Ads put a Google Verified badge above Search results. The channel works only when verification, lead review and weekly bid management are handled, and we own all three for businesses across the Phoenix metro and beyond.",
    howWeRunIt: [
      {
        title: "Full verification",
        body: "Background checks, license verification, insurance documentation, and ongoing compliance to keep the Google Verified badge active.",
      },
      {
        title: "Review every charged lead",
        body: "We review charged leads, rate bad ones with Rate this lead, and keep job types and service area accurate.",
      },
      {
        title: "Bid by job type, by geo",
        body: "Per-service, per-geography bid tuning based on close rates from your CRM, not Google's default flat structure.",
      },
      {
        title: "Reputation management",
        body: "Review acceleration and response cadence: LSA ranking is downstream of reviews.",
      },
    ],
    deliverables: [
      "Verification + Google Verified setup",
      "Weekly bid & geo optimization",
      "Lead review and credits",
      "Review acceleration playbook",
      "Monthly reporting w/ recovered spend",
    ],
    industriesFit: ["Home services", "Roofing", "Painting", "Pool services", "Landscaping", "Cleaning", "Tree services", "General contractors"],
    blueprint: "lsa",
  },
  {
    slug: "youtube-ads",
    idx: "04",
    title: "YouTube Ads",
    tag: "Bilingual (EN / ES)",
    to: "/services/youtube-ads",
    short:
      "Demand capture and brand presence in your market. In-stream and short-form video built for the buyer journey, service and consumer alike.",
    description:
      "Geo-targeted YouTube (in-stream, in-feed, and Shorts) produced for the buyer journey across service and consumer categories.",
    seoDescription:
      "In-stream, in-feed and Shorts campaigns fenced to the area you dispatch to, measured on branded search lift and assisted conversions, not view counts.",
    whatItIs:
      "YouTube alone is rarely a lead engine; alongside Google Search and Meta it compounds, lifting branded search volume in your market.",
    howWeRunIt: [
      {
        title: "Audience-first targeting",
        body: "Geo-fenced to your real market. No wasted impressions outside it.",
      },
      {
        title: "Creative for the real buyer",
        body: "In-stream skippable, in-feed, and Shorts formats, produced or sourced for your category, not generic corporate.",
      },
      {
        title: "Measured against Search lift",
        body: "We track branded-search lift and assisted conversions, not just view counts.",
      },
      {
        title: "Bilingual: EN / ES",
        body: "Native Spanish creative for US markets where your customer base warrants it.",
      },
    ],
    deliverables: [
      "Audience & geo strategy",
      "Creative brief + production sourcing",
      "Campaign build & launch",
      "Branded-search lift measurement",
      "Monthly reporting",
    ],
    industriesFit: ["Home services", "Consumer goods", "Food & beverage", "Sports & fitness", "Construction", "Roofing", "Landscaping"],
    blueprint: "youtube",
  },
  {
    slug: "seo-ai-search",
    idx: "05",
    title: "SEO & AI Search",
    tag: "",
    to: "/services/seo-ai-search",
    short:
      "A page for every question your customers ask, built for Google and for AI tools like ChatGPT, and measured by the leads it brings in.",
    description:
      "A page for every question your customers ask, built so Google sends people to each one and AI tools like ChatGPT can read them.",
    seoTitle: "SEO & AI Search for Home Services | Berk Hospitality Group",
    seoDescription:
      "A page for every question your customers ask, built so Google sends people to each one and AI tools like ChatGPT can read them. Measured by leads, not rankings.",
    whatItIs:
      "We run search for home services companies the way we run their ads: built on what people actually type, and judged by the leads it produces.",
    howWeRunIt: [],
    deliverables: [],
    industriesFit: [],
    blueprint: "google",
  },
];
